import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabViewModule } from 'primeng/tabview';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { firstValueFrom } from 'rxjs';
import { SubSink } from 'subsink';

import { dayjs } from '../../../../core/dayjs/dayjs';
import { FreyaCommonModule } from '../../../../freya-common/freya-common.module';
import { DispatchActions } from '../../store/dispatch.actions';
import { CrewScheduleSendStatus } from '../../store/dispatch.reducer';
import {
    selectDispatchDate,
    selectSendScheduleAssetNotes,
    selectSendScheduleCrewNotes,
    selectSendSchedulePreviews,
    selectSendScheduleRouteTree,
    selectSendScheduleSelectedCount,
    selectSendScheduleSubmitting,
} from '../../store/dispatch.selectors';
import { generateCrewScheduleHash } from '../../utils/schedule-hash';
import { DispatchRouteThreadsComponent } from '../dispatch-route-threads/dispatch-route-threads.component';

export interface RouteCrewItem {
    crewMember: any;
    isSelected: boolean;
    sendStatus: CrewScheduleSendStatus;
    email: string;
    phone: string;
    hasValidEmail: boolean;
    hasValidPhone: boolean;
    canSend: boolean;
}

export interface RouteGroup {
    routeName: string;
    assetId: string;
    assetIds: string[];
    events: any[];
    crew: RouteCrewItem[];
    hasMissingCrew: boolean;
    threads: any[];
}

@Component({
    selector: 'app-dispatch-send-schedule-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        FreyaCommonModule,
        AccordionModule,
        ButtonModule,
        CheckboxModule,
        DividerModule,
        DispatchRouteThreadsComponent,
        InputTextareaModule,
        ProgressSpinnerModule,
        TabViewModule,
        TagModule,
        TooltipModule,
    ],
    templateUrl: './dispatch-send-schedule-dialog.component.html',
    styleUrl: './dispatch-send-schedule-dialog.component.scss',
})
export class DispatchSendScheduleDialogComponent implements OnInit, OnDestroy {

    @Output() closeDialog = new EventEmitter<void>();

    routeTree$ = this.store.select(selectSendScheduleRouteTree);
    assetNotes$ = this.store.select(selectSendScheduleAssetNotes);
    crewNotes$ = this.store.select(selectSendScheduleCrewNotes);
    previews$ = this.store.select(selectSendSchedulePreviews);
    selectedCount$ = this.store.select(selectSendScheduleSelectedCount);
    isSubmitting$ = this.store.select(selectSendScheduleSubmitting);

    /** Currently selected crew member for preview */
    selectedPreviewKey: string | null = null;
    selectedPreviewAssetId: string | null = null;
    selectedPreviewUserId: string | null = null;
    activePreviewTab: number = 0; // 0=Email, 1=SMS

    routeTree: RouteGroup[] = [];
    assetNotes: Record<string, string> = {};
    crewNotes: Record<string, string> = {};
    previews: Record<string, { emailHtml: string; emailSubject: string; smsBody: string; loading: boolean }> = {};

    /**
     * Cached srcdoc SafeHtml values keyed by preview key.
     * Prevents the iframe from re-loading on every change detection cycle —
     * bypassSecurityTrustHtml must only be called when the HTML actually changes.
     */
    private srcdocCache = new Map<string, { html: string; safe: SafeHtml }>();

    private subs = new SubSink();

    constructor(private store: Store, private sanitizer: DomSanitizer) {}

    ngOnInit(): void {
        this.subs.sink = this.routeTree$.subscribe((tree) => {
            this.routeTree = tree;
        });
        this.subs.sink = this.assetNotes$.subscribe((notes) => {
            this.assetNotes = notes;
        });
        this.subs.sink = this.crewNotes$.subscribe((notes) => {
            this.crewNotes = notes;
        });
        this.subs.sink = this.previews$.subscribe((previews) => {
            this.previews = previews;
        });

    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    toggleCrew(userId: string, assetId: string, selected: boolean): void {
        this.store.dispatch(DispatchActions.sendScheduleCrewToggled({ userId, assetId, selected }));
    }

    selectAllChanged(): void {
        this.store.dispatch(DispatchActions.sendScheduleAllChangedToggled());
    }

    deselectAll(): void {
        this.store.dispatch(DispatchActions.sendScheduleDeselectAll());
    }

    onAssetNoteChange(assetId: string, note: string): void {
        this.store.dispatch(DispatchActions.sendScheduleCustomNoteChanged({ assetId, note }));
        // Re-fetch preview if the currently previewed route is the one whose note changed
        if (this.selectedPreviewAssetId === assetId && this.selectedPreviewUserId) {
            this.requestPreview(assetId, this.selectedPreviewUserId);
        }
    }

    onCrewNoteChange(userId: string, note: string): void {
        this.store.dispatch(DispatchActions.sendScheduleCrewCustomNoteChanged({ userId, note }));
        // Re-fetch preview if this crew member is selected
        if (this.selectedPreviewUserId === userId && this.selectedPreviewAssetId) {
            this.requestPreview(this.selectedPreviewAssetId, userId);
        }
    }

    selectCrewForPreview(assetId: string, userId: string): void {
        this.selectedPreviewAssetId = assetId;
        this.selectedPreviewUserId = userId;
        this.selectedPreviewKey = `${assetId}:${userId}`;
        this.requestPreview(assetId, userId);
    }

    private requestPreview(assetId: string, userId: string): void {
        this.store.dispatch(DispatchActions.sendSchedulePreviewRequested({ assetId, userId }));
    }

    getPreview(assetId: string, userId: string) {
        const key = `${assetId}:${userId}`;
        return this.previews[key] || null;
    }

    get currentPreview() {
        if (!this.selectedPreviewKey) { return null; }
        return this.previews[this.selectedPreviewKey] || null;
    }

    /**
     * Returns a cached SafeHtml value for use as iframe srcdoc.
     * Uses a cache keyed by the raw HTML string to avoid calling
     * bypassSecurityTrustHtml on every change detection cycle, which would
     * assign a new object reference to [srcdoc] and cause the iframe to reload
     * (visible as constant flashing).
     */
    getEmailPreviewSrcdoc(emailHtml: string): SafeHtml {
        const cached = this.srcdocCache.get(this.selectedPreviewKey);
        if (cached?.html === emailHtml) {
            return cached.safe;
        }
        const head = '<meta charset="UTF-8">'
            + '<meta name="viewport" content="width=device-width,initial-scale=1">';
        const doc = `<!DOCTYPE html><html><head>${head}</head>`
            + `<body style="margin:0;padding:0">${emailHtml}</body></html>`;
        const safe = this.sanitizer.bypassSecurityTrustHtml(doc);
        if (this.selectedPreviewKey) {
            this.srcdocCache.set(this.selectedPreviewKey, { html: emailHtml, safe });
        }
        return safe;
    }

    /** Auto-resize the iframe to fit its content height after load */
    onEmailFrameLoad(event: Event): void {
        const iframe = event.target as HTMLIFrameElement;
        try {
            const height = iframe.contentDocument?.body?.scrollHeight;
            if (height) {
                iframe.style.height = `${height + 20}px`;
            }
        } catch {
            // cross-origin or sandbox restriction — ignore
        }
    }

    getStatusSeverity(status: CrewScheduleSendStatus): 'success' | 'warning' | 'danger' | 'secondary' {
        switch (status) {
            case 'up-to-date': return 'success';
            case 'changed': return 'warning';
            case 'removed': return 'danger';
            case 'not-sent': return 'secondary';
        }
    }

    getStatusLabel(status: CrewScheduleSendStatus): string {
        switch (status) {
            case 'up-to-date': return 'Sent';
            case 'changed': return 'Changed';
            case 'removed': return 'Removed';
            case 'not-sent': return 'Not sent';
        }
    }

    getStatusIcon(status: CrewScheduleSendStatus): string {
        switch (status) {
            case 'up-to-date': return 'pi pi-check';
            case 'changed': return 'pi pi-exclamation-triangle';
            case 'removed': return 'pi pi-times';
            case 'not-sent': return 'pi pi-circle';
        }
    }

    async send(): Promise<void> {
        // Use the dispatch date from the store rather than wall-clock so that
        // opening yesterday's or tomorrow's schedule still submits the correct date.
        const dispatchDate = await firstValueFrom(this.store.select(selectDispatchDate));
        const date = dayjs(dispatchDate).format('YYYY-MM-DD');

        // One batch id per send() click: per-route submits report into it and
        // sent-draft cleanup waits until every route reports back successfully
        // (CodeRabbit ead65a67 — a later in-flight route still needs drafts).
        const batchId = `send-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
        const sendableRoutes = this.routeTree.filter((route) =>
            route.crew.some((c) => c.isSelected && c.canSend));
        if (sendableRoutes.length) {
            this.store.dispatch(DispatchActions.sendScheduleBatchStarted({
                batchId, routeCount: sendableRoutes.length,
            }));
        }

        for (const route of this.routeTree) {
            // Only include selected crew who have at least one valid contact method
            const selectedOnRoute = route.crew.filter((c) => c.isSelected && c.canSend);
            if (!selectedOnRoute.length) { continue; }

            // Per-crew schedule hash (same algorithm the backend documents in
            // schedule-hash.utils.ts) so 'Changed' badges can compare against
            // the hash stored with the previous send. Crew not on any event of
            // this route get an empty-schedule hash (still meaningful).
            const hashByUserId = new Map<string, string>();
            for (const crewItem of selectedOnRoute) {
                const userId = crewItem.crewMember.user.id;
                const crewEvents = route.events.filter((e) =>
                    (e.crew || []).some((m: any) => m.user?.id === userId),
                );
                // eslint-disable-next-line no-await-in-loop
                hashByUserId.set(userId, await generateCrewScheduleHash(
                    userId,
                    date,
                    crewEvents.map((e) => ({
                        id: e.event.id,
                        start: e.event.start || 0,
                        end: e.event.end || 0,
                        type: e.event.type,
                        locationIds: (e.event.locations || []).map((l: any) => l.id || l.locationId).filter(Boolean),
                        assetIds: (e.event.assets || []).map((a: any) => a.id).filter(Boolean),
                        attendeeIds: (e.event.attendees || []).map((a: any) => a.user?.id || a.userId).filter(Boolean),
                    })),
                ));
            }

            this.store.dispatch(DispatchActions.sendScheduleSubmitted({
                assetId: route.assetId,
                batchId,
                date,
                recipients: selectedOnRoute.map((c) => ({
                    userId: c.crewMember.user.id,
                    scheduleHash: hashByUserId.get(c.crewMember.user.id) || '',
                    customNoteForCrew: this.crewNotes[c.crewMember.user.id] || undefined,
                })),
                customNoteForAsset: this.assetNotes[route.assetId] || undefined,
            }));
        }
    }

    get selectedWithNoContact(): RouteCrewItem[] {
        const result: RouteCrewItem[] = [];
        for (const route of this.routeTree) {
            for (const c of route.crew) {
                if (c.isSelected && !c.canSend) { result.push(c); }
            }
        }
        return result;
    }

    get selectedWithNoContactNames(): string {
        return this.selectedWithNoContact.map((c) => c.crewMember.name).join(', ');
    }

    close(): void {
        this.store.dispatch(DispatchActions.sendScheduleDialogClosed());
        this.closeDialog.emit();
    }

    trackByRouteId(_: number, route: RouteGroup): string {
        return route.assetId;
    }

    trackByCrewId(_: number, crew: RouteCrewItem): string {
        return crew.crewMember.user.id;
    }
}
